import { useState } from 'react';
import { Check, Sparkles } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/use-auth';

/** Versión actual del panel de novedades. Al subirla, reaparece para todos. */
export const WHATS_NEW_VERSION = '2026.08';

const NEWS: string[] = [
  'Ya puedes corregir un día en el que no fichaste nada: ábrelo en "Mi jornada" y añade la entrada y la salida que faltan. Antes esos días no se podían tocar.',
  'Las correcciones de fichaje ya no tienen límite de antigüedad: se puede corregir cualquier día pasado.',
  'Nuevo tipo de ausencia "Horas justificadas": pide libre un tramo de horas de un día concreto (por ejemplo, de 10:00 a 12:00) e indica si entregas justificante. Con justificante esas horas no gastan vacaciones; sin él se descuenta la parte proporcional.',
  'Las ausencias solo se pueden cancelar antes de que empiecen. Una vez disfrutadas quedan en el registro, para que cancelarlas no devuelva días ya usados.',
  'La aplicación se actualiza sola: cuando se publica una versión nueva, la página se recarga sin que tengas que refrescar a mano.',
];

/**
 * Modal informativo que aparece la primera vez que un usuario entra tras la
 * actualización (`user.whatsNewVersionSeen !== WHATS_NEW_VERSION`). Se muestra
 * solo después de haber aceptado la política de privacidad, para no solaparse
 * con `<PrivacyPolicyGate />`. Al pulsar "Entendido" se marca la versión como
 * vista en el backend; si la llamada falla, el panel se cierra igualmente para
 * no bloquear al usuario.
 */
export function WhatsNewPanel() {
  const { user, markWhatsNewSeen } = useAuth();
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const shouldShow =
    user !== null &&
    user.privacyPolicyAcceptedAt !== null &&
    user.whatsNewVersionSeen !== WHATS_NEW_VERSION;

  if (!shouldShow || dismissed) return null;

  async function onAck() {
    setSaving(true);
    try {
      await markWhatsNewSeen(WHATS_NEW_VERSION);
    } catch {
      // No bloqueamos al usuario si falla el guardado; cerramos igualmente.
    } finally {
      setSaving(false);
      setDismissed(true);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
      style={{ backgroundColor: 'rgba(10,14,31,0.45)' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="whats-new-title"
    >
      <div className="bg-card border border-border rounded-sm shadow-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        <header className="px-7 py-5 border-b border-border flex items-center gap-3">
          <Sparkles className="h-5 w-5 text-icaria-600 shrink-0" />
          <h2 id="whats-new-title" className="text-lg font-semibold tracking-tightish">
            Novedades de esta versión
          </h2>
        </header>

        <div className="flex-1 overflow-y-auto px-7 py-5">
          <ul className="flex flex-col gap-3">
            {NEWS.map((item) => (
              <li key={item} className="flex items-start gap-3 text-[14px] leading-relaxed">
                <Check className="h-4 w-4 mt-0.5 text-icaria-600 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <footer className="px-7 py-4 border-t border-border bg-paper flex items-center justify-end gap-4 flex-wrap">
          <Button type="button" variant="primary" onClick={onAck} disabled={saving}>
            {saving ? 'Guardando…' : 'Entendido'}
          </Button>
        </footer>
      </div>
    </div>
  );
}
